// -----------------------------------------------------------------------------
// Client-side "job lock" persistence.
//
// The backend doesn't yet expose a `locked` field on `IJob` (see
// `docs/pending-endpoints.md → PUT /job/:id`). Until it does, we persist the
// lock state in `localStorage` and mirror changes to listeners via a
// `CustomEvent` so any mounted view re-renders in sync.
//
// Semantics: a locked job cannot be (re-)assigned to a mechanic via the
// Awaiting Dismantling right-click menu, and its tile is excluded from the
// FullCalendar Draggable item selector so it cannot be dragged onto the
// planner either. Locking is additive to assignment — it does not clear the
// existing `mechanicId`.
// -----------------------------------------------------------------------------

import { useEffect, useState, useCallback } from "react";

const LOCAL_STORAGE_KEY = "wilma.jobLocks.v1";
const CHANGE_EVENT = "wilma:jobLocks:change";

type JobLockMap = Record<string, true>;

const readStore = (): JobLockMap => {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const writeStore = (next: JobLockMap) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(next));
    // Notify any subscribed components in the current tab. `storage` events
    // only fire cross-tab, so we emit our own event for same-tab updates.
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
  } catch {
    // Quota / private-mode errors: silently swallow — locking is a
    // best-effort UX nicety, not a correctness boundary.
  }
};

export function isJobLocked(jobId: string): boolean {
  return !!readStore()[jobId];
}

export function setJobLocked(jobId: string, locked: boolean): void {
  const store = readStore();
  if (locked) {
    store[jobId] = true;
  } else {
    delete store[jobId];
  }
  writeStore(store);
}

export function getLockedJobIds(): Set<string> {
  return new Set(Object.keys(readStore()));
}

/**
 * Subscribes a component to the set of locked job IDs. Re-renders when any
 * job is (un)locked in the current tab or via another tab's `storage` event.
 */
export function useLockedJobs() {
  const [lockedIds, setLockedIds] = useState<Set<string>>(() =>
    getLockedJobIds()
  );

  useEffect(() => {
    const refresh = () => setLockedIds(getLockedJobIds());
    window.addEventListener(CHANGE_EVENT, refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(CHANGE_EVENT, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  const isLocked = useCallback(
    (jobId: string) => lockedIds.has(jobId),
    [lockedIds]
  );

  return { lockedIds, isLocked };
}
